import React from "react";

import { Group } from "./Group.tsx";
import { Separator } from "../../../../Components/ui/separator.tsx";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../../Components/ui/select.tsx";
import { Button } from "../../../../Components/ui/button.tsx";
import { CopyIcon, PlusIcon, TrashIcon } from "lucide-react";
import { cn } from "../../../../lib/utils.js";
import { ActionType } from "../../types/zustand.ts";
import { FieldIcon } from "../FieldIcon.tsx";
import { truncate } from "../../utils/string.ts";
import { useFormEditorContext } from "../../../../Contexts/FormEditorContext.jsx";
import { useLogicV2 } from "../../hooks/use-logic-v2.ts";

type BranchProps = {
    id: string;
};

export function Branch({ id }: BranchProps) {
    const { fields, logics, selectedField, endScreen } = useFormEditorContext();
    const { dispatch } = useLogicV2();
    const activeFieldId = selectedField?.id;
    const logic = logics.find((logic) => logic.fieldId === activeFieldId);

    if (!logic) return null;

    const branch = logic.branches.find((branch) => branch.id === id);

    if (!branch) return null;

    const getApplicableFields = () => {
        // This returns all the fields that are positioned after the current active field
        const activeFieldIndex = fields.findIndex(
            (field) => field.id === activeFieldId,
        );

        const startIndex = activeFieldIndex + 1;

        const applicableFields = fields
            .slice(startIndex)
            ?.map((field, index) => ({
                ...field,
                index: index + 1 + startIndex,
            }));

        // Add end screen to the applicable fields if it is present
        const hasEndScreen = !!endScreen?.id;

        if (hasEndScreen) {
            endScreen.type = "end_screen";
            applicableFields.push(endScreen);
        }

        return applicableFields;
    };

    const handleAddGroup = () => {
        dispatch({
            type: ActionType.ADD_GROUP,
            payload: {
                branchId: id,
            },
        });
    };

    const handleOnChange = ({ targetId }: { targetId: string }) => {
        dispatch({
            type: ActionType.EDIT_BRANCH,
            payload: {
                branchId: id,
                targetId: targetId,
            },
        });
    };

    const handleRemoveBranch = () => {
        dispatch({
            type: ActionType.REMOVE_BRANCH,
            payload: {
                branchId: id,
            },
        });
    };

    const handleDuplicateGroup = () => {
        dispatch({
            type: ActionType.DUPLICATE_BRANCH,
            payload: {
                branchId: id,
            },
        });
    };

    return (
        <div className="bg-gray-50 rounded-[8px] p-2 border border-gray-200 flex flex-col w-full items-start">
            <div className="flex flex-row items-center justify-between w-full">
                <p className="text-sm font-medium">{branch.name}</p>
                <div className="flex flex-row items-center">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="w-[27px] h-[27px]"
                        onClick={handleDuplicateGroup}
                    >
                        <CopyIcon className="w-4 h-4 text-gray-700" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="w-[27px] h-[27px]"
                        onClick={handleRemoveBranch}
                    >
                        <TrashIcon className="w-4 h-4 text-gray-700" />
                    </Button>
                </div>
            </div>
            <div className="mt-2 flex flex-col gap-2 w-full">
                {branch.groups.map((group) => (
                    <React.Fragment key={group.id}>
                        <Group
                            key={group.id}
                            id={group.id}
                            branchId={branch.id}
                        />
                    </React.Fragment>
                ))}
            </div>
            {branch.groups.length > 0 && <GroupDivider className="mt-3" />}
            <Button
                variant="outline"
                className="mt-3 rounded-[6px] py-[6px] px-2 text-xs text-[#191B33] font-medium w-fit h-fit"
                onClick={handleAddGroup}
            >
                <PlusIcon className="w-4 h-4 mr-[3px] text-gray-700" />
                Add Rule
            </Button>
            <div className="flex flex-col items-start bg-white rounded-[8px] p-2 mt-3 w-full">
                <p className="text-xs font-medium">THEN {`->`} GO TO</p>
                <Select
                    value={branch.targetId}
                    onValueChange={(value) =>
                        handleOnChange({ targetId: value })
                    }
                >
                    <SelectTrigger className="h-9 mt-2">
                        <SelectValue
                            placeholder="Select..."
                            className="text-[8px]"
                        />
                    </SelectTrigger>
                    <SelectContent className="w-[256px]">
                        {getApplicableFields().map((field) => (
                            <SelectItem
                                key={field.id}
                                value={field.id}
                                className="p-[7px]"
                            >
                                <div className="flex flex-row items-center gap-2">
                                    <FieldIcon
                                        type={field.type}
                                        index={field.index}
                                        className="h-[22px] w-9 p-[5px] text-[8px]"
                                        displayIndex={
                                            field.type !== "end_screen" &&
                                            field.type !== "welcome_screen"
                                        }
                                    />
                                    <p className="text-xs text-[#191B33]">
                                        {truncate(field.title, 20)}
                                    </p>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}

type GroupDividerProps = React.HTMLAttributes<HTMLDivElement>;

function GroupDivider({ className, ...props }: GroupDividerProps) {
    return (
        <div
            className={cn(
                "flex flex-row items-center justify-center gap-2",
                className,
            )}
            {...props}
        >
            <Separator className="flex-1" />
            <p className="text-sm font-medium text-[#191B33]">OR</p>
            <Separator className="flex-1" />
        </div>
    );
}
